import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseCellComponent } from '../base-cell.component';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DataTableSchema, DataSchemaDataType } from 'src/app/services/models/datasets.models';
import { ViewTab, ViewManager, CellFilter, ContextObjectConfig, CreateEmptyContextConfig } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableFilters, MatchMode, SortCriteria } from 'src/app/services/models/dynamic-table.models';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { toContextArray, isListContext } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { Subject, takeUntil, debounceTime, switchMap, EMPTY, forkJoin, of, Observable, tap, catchError } from 'rxjs';
import { LookupDialogComponent, LookupDialogData, LookupDialogResult } from '../input-field-cell/lookup-dialog/lookup-dialog.component';

interface TableColumn {
  fieldName: string;
  displayName: string;
  dataType: string;
}

interface TableData {
  [key: string]: any;
}

@Component({
  selector: 'bhub-filter-list-cell',
  standalone: false,
  templateUrl: './filter-list-cell.component.html',
  styleUrl: './filter-list-cell.component.scss'
})
export class FilterListCellComponent extends BaseCellComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tableHeader', { static: false }) tableHeader!: ElementRef<HTMLDivElement>;
  @ViewChild('tableBody', { static: false }) tableBody!: ElementRef<HTMLDivElement>;
  @ViewChild('filterDialog') filterDialog!: TemplateRef<any>;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @Input() availableTabs: ViewTab[] = [];
  @Input() viewManager: ViewManager | null = null;
  @Output() tabNavigation = new EventEmitter<string>();
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  columns: TableColumn[] = [];
  tableData: TableData[] = [];
  filteredData: TableData[] = [];
  displayedColumns: string[] = [];
  isLoadingData = false;

  // Sorting
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;

  // Filter properties (similar to Data Entry component)
  filterRows: {
    fieldId: number | null,
    fieldName: string,
    value: string,
    value2?: string,  // For 'between' match mode
    dateValue?: Date | null,  // For DateTime fields
    dateValue2?: Date | null,  // For DateTime fields with 'between' mode
    matchMode: MatchMode,
    dataType: string,
    availableMatchModes?: { label: string, value: MatchMode }[],
    isPreset?: boolean
  }[] = [];
  hasActiveFilters = false;
  filterDisplayMode: 'dialog' | 'inline' = 'dialog';

  selectedRowIndex: number | null = null; // Track selected row for checkbox selection
  navigationHighlightedRowIndex: number | null = null; // Track row with navigation context data

  // Pagination
  paginationEnabled = false;
  currentPage = 0;
  pageSize = 100;
  totalRecords = 0;

  private datasetSchemas: DataTableSchema[] = []; // Store original schemas for reference metadata
  private destroy$ = new Subject<void>();
  private loadDataset$ = new Subject<number>();
  private currentDatasetId: number | null = null;
  private inlineFilterApply$ = new Subject<void>();

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService,
    private dialog: MatDialog
  ) {
    super();

    // Set up debounced inline filter apply
    this.inlineFilterApply$
      .pipe(
        debounceTime(1750),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyInlineFilters();
      });

    // Set up debounced dataset loading to prevent duplicate requests
    this.loadDataset$
      .pipe(
        debounceTime(100), // Wait 100ms between requests
        switchMap(datasetId => {
          if (datasetId === this.currentDatasetId) {
            return EMPTY;
          }
          this.currentDatasetId = datasetId;
          this.isLoadingData = true;
          return this.datasetService.getDatasetReferences();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          if (this.currentDatasetId) {
            const dataset = datasetsMap.get(this.currentDatasetId);
            if (dataset) {
              this.setupTableColumns(dataset.dataTableSchemas);
              this.initPresetFilters();
              this.restoreFilterState();
              this.loadTableData(this.currentDatasetId);
            } else {
              this.isLoadingData = false;
            }
          }
        },
        error: (error) => {
          console.error('Error loading dataset:', error);
          this.isLoadingData = false;
          this.currentDatasetId = null;
        }
      });
  }

  ngOnDestroy(): void {
    // Only save filter state if the component actually finished loading (has columns).
    // A component that was created and destroyed before loading shouldn't overwrite saved state.
    if (this.columns.length > 0) {
      this.saveFilterState();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private saveFilterState(): void {
    if (!this.cell) return;
    if (this.filterRows.length > 0) {
      this.cell.configuration._activeFilterState = this.filterRows.map(r => ({ ...r }));
      this.cell.configuration._activeFilterHasActive = this.hasActiveFilters;
    } else {
      delete this.cell.configuration._activeFilterState;
      delete this.cell.configuration._activeFilterHasActive;
    }
  }

  ngAfterViewInit(): void {
    // Synchronize horizontal scrolling between header and body
    if (this.tableHeader && this.tableBody) {
      const headerElement = this.tableHeader.nativeElement;
      const bodyElement = this.tableBody.nativeElement;

      bodyElement.addEventListener('scroll', () => {
        headerElement.scrollLeft = bodyElement.scrollLeft;
      });

      headerElement.addEventListener('scroll', () => {
        bodyElement.scrollLeft = headerElement.scrollLeft;
      });
    }
  }

  protected initializeCell(): void {
    this.filterDisplayMode = this.getConfigValue('filterDisplayMode', 'dialog') as 'dialog' | 'inline';

    // Initialize context if it doesn't exist
    this.initializeContext();

    if (this.cell?.dataBinding?.datasetId) {
      this.loadDataset$.next(this.cell.dataBinding.datasetId);
    } else {
      // Reset to empty state when no dataset is configured
      this.columns = [];
      this.tableData = [];
      this.filteredData = [];
      this.displayedColumns = [];
      this.currentDatasetId = null;
    }
  }

  private initializeContext(): void {
    if (!this.viewManager) return;

    // Ensure the context data object exists
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    // Initialize primary context
    const enablePrimaryContext = this.getConfigValue('enablePrimaryContext', false);
    const primaryContextVariable = this.getConfigValue('primaryContextVariable', '');

    if (enablePrimaryContext && primaryContextVariable) {
      if (!this.viewManager.contextData.hasOwnProperty(primaryContextVariable)) {
        this.viewManager.contextData[primaryContextVariable] = this.createEmptyContextObject();
      }
    }

    // Initialize additional contexts
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    if (additionalContexts && additionalContexts.length > 0) {
      // Initialize contextInstructions if it doesn't exist
      if (!this.viewManager.contextInstructions) {
        this.viewManager.contextInstructions = {};
      }

      additionalContexts.forEach(additionalConfig => {
        if (additionalConfig.contextVariable && additionalConfig.contextVariable.trim()) {
          const contextKey = additionalConfig.contextVariable;

          // Initialize context data
          if (!this.viewManager.contextData.hasOwnProperty(contextKey)) {
            this.viewManager.contextData[contextKey] = this.createEmptyContextObject();
          }

          // Always rebuild the context instruction from the CURRENT config so that
          // edits to the additionalContext (filters, limit, dataset, etc.) take
          // effect on next mount. The previous `!hasOwnProperty` guard kept stale
          // instructions alive after the admin changed the config.
          this.viewManager!.contextInstructions[contextKey] = this.buildContextInstruction(additionalConfig);
        }
      });
    }

    // Fallback to legacy single context variable for backward compatibility
    const legacyContextKey = this.getConfigValue('contextVariable', '');
    if (legacyContextKey && !enablePrimaryContext && (!additionalContexts || additionalContexts.length === 0)) {
      if (!this.viewManager.contextData.hasOwnProperty(legacyContextKey)) {
        this.viewManager.contextData[legacyContextKey] = this.createEmptyContextObject();
      }
    }
  }

  private createEmptyContextObject(): any {
    // If we have columns defined, create an object with all fields set to null
    if (this.columns && this.columns.length > 0) {
      const emptyObject: any = {};
      this.columns.forEach(column => {
        emptyObject[column.fieldName] = null;
      });

      // Add metadata structure — mark as empty so action buttons know to add, not update
      emptyObject.__metadata = {
        datasetId: this.cell?.dataBinding?.datasetId || null,
        fieldReferences: this.buildFieldReferences(),
        isEmptyContext: true
      };

      return emptyObject;
    }
    // If no columns yet, return null (will be updated when dataset loads)
    return null;
  }

  protected onCellOrDataChange(): void {
    // Only reload if the dataset ID actually changed
    const newDatasetId = this.cell?.dataBinding?.datasetId;

    if (newDatasetId && newDatasetId !== this.currentDatasetId) {
      this.loadDataset$.next(newDatasetId);
    } else if (!newDatasetId && this.currentDatasetId) {
      // Dataset was cleared - reset state
      this.columns = [];
      this.tableData = [];
      this.filteredData = [];
      this.displayedColumns = [];
      this.currentDatasetId = null;
    } else if (newDatasetId === this.currentDatasetId && this.hasCellFilters()) {
      // Same dataset but cell has filters - reload data to apply new filter values
      this.loadTableData(newDatasetId);
    }
  }

  private hasCellFilters(): boolean {
    return this.cellFilterService.hasCellFilters(this.cell?.dataBinding?.cellFilters);
  }

  protected onCellUpdate(): void {
    this.initializeCell();
  }

  protected onDataSet(data: any): void {
    // Handle external data if needed
    // Also restore selection state if data has changed
    this.restoreSelectionState();
  }


  private setupTableColumns(schemas: DataTableSchema[]): void {
    // Store original schemas for reference metadata
    this.datasetSchemas = schemas;

    // Set up all available columns (for context structure)
    this.columns = schemas
      .filter(schema => !schema.isHidden)
      .map(schema => ({
        fieldName: schema.fieldName,
        displayName: schema.displayName || schema.fieldName,
        dataType: schema.dataType.toString()
      }));

    // Set displayed columns based on configuration
    const configuredDisplayColumns = this.getConfigValue('displayColumns', []) as string[];

    if (configuredDisplayColumns.length > 0) {
      // Only show columns that are configured to be displayed
      this.displayedColumns = configuredDisplayColumns.filter(colName =>
        this.columns.some(col => col.fieldName === colName)
      );
    } else {
      // If no display columns configured, show all columns
      this.displayedColumns = this.columns.map(col => col.fieldName);
    }

    // Reset filters when columns change
    this.filterRows = [];
    this.hasActiveFilters = false;

    // Update context structure with ALL field names (not just displayed ones)
    this.updateContextStructure();
  }

  private initPresetFilters(): void {
    const presetFields = this.getConfigValue('presetFilterFields', []) as string[];
    if (!presetFields || presetFields.length === 0 || this.columns.length === 0) return;

    // Only add presets that aren't already in filterRows
    const existingPresets = this.filterRows.filter(r => r.isPreset).map(r => r.fieldName);

    const newPresetRows: typeof this.filterRows = [];
    presetFields.forEach(fieldName => {
      if (existingPresets.includes(fieldName)) return;
      const schema = this.datasetSchemas.find(s => s.fieldName === fieldName);
      if (schema) {
        const dataType = schema.dataType || DataSchemaDataType.String;
        const configuredMatchMode = this.filterDisplayMode === 'inline'
          ? this.getConfigValue('inlineFilterMatchMode', '') as string
          : '';
        newPresetRows.push({
          fieldId: schema.fieldId || null,
          fieldName: schema.fieldName,
          value: '',
          value2: '',
          dateValue: null,
          dateValue2: null,
          matchMode: (configuredMatchMode || (this.isNumericOrDateType(dataType) ? 'exact' : 'partial')) as MatchMode,
          dataType: dataType,
          availableMatchModes: this.getAvailableMatchModesForField(dataType),
          isPreset: true
        });
      }
    });
    // Prepend in configured order
    this.filterRows.unshift(...newPresetRows);
  }

  private restoreFilterState(): void {
    const saved = this.cell?.configuration?._activeFilterState;
    if (!saved || !Array.isArray(saved) || saved.length === 0) return;

    this.filterRows = saved.map((r: any) => ({
      ...r,
      availableMatchModes: r.dataType ? this.getAvailableMatchModesForField(r.dataType) : []
    }));
    this.hasActiveFilters = this.cell.configuration._activeFilterHasActive || false;
  }

  private updateContextStructure(): void {
    if (!this.viewManager || !this.viewManager.contextData) return;

    // Update primary context structure if enabled
    const enablePrimaryContext = this.getConfigValue('enablePrimaryContext', false);
    const primaryContextVariable = this.getConfigValue('primaryContextVariable', '');

    if (enablePrimaryContext && primaryContextVariable) {
      const currentPrimaryContext = this.viewManager.contextData[primaryContextVariable];
      // Only update if context hasn't been populated with real data yet
      if (currentPrimaryContext === null || (typeof currentPrimaryContext === 'object' && !this.hasRealData(currentPrimaryContext))) {
        this.viewManager.contextData[primaryContextVariable] = this.createEmptyContextObject();

        // Emit the updated context structure
        this.contextDataChange.emit({
          [primaryContextVariable]: this.viewManager.contextData[primaryContextVariable]
        });
      }
    }

    // Update legacy context variable structure for backward compatibility
    const contextKey = this.getConfigValue('contextVariable', '');
    if (contextKey && contextKey !== primaryContextVariable) {
      // Only update if context hasn't been populated with real data yet
      const currentContext = this.viewManager.contextData[contextKey];
      if (currentContext === null || (typeof currentContext === 'object' && !this.hasRealData(currentContext))) {
        // Create object with all fields from dataset schema
        this.viewManager.contextData[contextKey] = this.createEmptyContextObject();

        // Emit the updated context structure
        this.contextDataChange.emit({
          [contextKey]: this.viewManager.contextData[contextKey]
        });
      }
    }
  }

  private hasRealData(obj: any): boolean {
    // Check if object has any non-null values (indicating real data has been selected)
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some(value => value !== null && value !== undefined);
  }

  private buildCellFilters(): DynamicTableFilters | undefined {
    return this.cellFilterService.buildCellFilters(this.cell?.dataBinding?.cellFilters, this.viewManager);
  }

  /**
   * Split a filter input on commas to support filtering by multiple values in a
   * single field. e.g. "2466, 2467" → ["2466", "2467"]. Trims whitespace and
   * drops empty pieces. A value with no commas just returns a one-element array.
   */
  private splitMultiValueInput(raw: string | null | undefined): string[] {
    if (!raw) return [];
    return raw.split(',').map(v => v.trim()).filter(v => v.length > 0);
  }

  private buildUserFilters(): DynamicTableFilters | undefined {
    // Convert filter rows to DynamicTableFilters format
    const filters: DynamicTableFilters = {};

    this.filterRows.forEach(filterRow => {
      if (!filterRow.fieldId) return;

      // Check if filter has a value
      const hasValue = this.isDateType(filterRow.dataType)
        ? filterRow.dateValue != null
        : filterRow.value?.trim();

      if (!hasValue) return;

      let values: (string | number)[] = [];

      if (filterRow.matchMode === 'between') {
        const hasValue2 = this.isDateType(filterRow.dataType)
          ? filterRow.dateValue2 != null
          : filterRow.value2?.trim();

        if (!hasValue2) return; // Between requires two values

        if (this.isNumericType(filterRow.dataType)) {
          values = [parseFloat(filterRow.value), parseFloat(filterRow.value2!)];
        } else if (this.isDateType(filterRow.dataType)) {
          values = [filterRow.value.trim(), filterRow.value2!.trim()];
        } else {
          values = [filterRow.value.trim(), filterRow.value2!.trim()];
        }
      } else {
        if (this.isDateType(filterRow.dataType)) {
          // Date inputs come from a picker — no comma-split, just a single value
          values = [filterRow.value.trim()];
        } else if (this.isNumericType(filterRow.dataType)) {
          values = this.splitMultiValueInput(filterRow.value)
            .map(v => parseFloat(v))
            .filter(n => !isNaN(n));
        } else {
          values = this.splitMultiValueInput(filterRow.value);
        }

        if (values.length === 0) return;
      }

      filters[filterRow.fieldId] = {
        values: values,
        matchMode: filterRow.matchMode
      };
    });

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private mergeFilters(cellFilters: DynamicTableFilters | undefined, userFilters: DynamicTableFilters | undefined): DynamicTableFilters | undefined {
    if (!cellFilters && !userFilters) return undefined;
    if (!cellFilters) return userFilters;
    if (!userFilters) return cellFilters;

    // Merge both filter sets - user filters take precedence for same fieldId
    return { ...cellFilters, ...userFilters };
  }

  private loadTableData(datasetId: number, offset?: number): void {
    // Reset to first page when loading without explicit offset (e.g., filter change, initial load)
    if (offset === undefined) {
      this.currentPage = 0;
    }

    // Check if any configured dynamic filters failed to resolve — if so, show empty rather than unfiltered data
    if (this.cellFilterService.hasUnresolvedFilters(this.cell?.dataBinding?.cellFilters, this.viewManager)) {
      this.tableData = [];
      this.filteredData = [];
      this.isLoadingData = false;
      return;
    }

    // Build filters from cell configuration and user filter rows
    const cellFilters = this.buildCellFilters();
    const userFilters = this.buildUserFilters();
    const filters = this.mergeFilters(cellFilters, userFilters);

    this.isLoadingData = true;

    // Use the DynamicTableService to get real data from the select-entries endpoint
    const limit = this.getConfigValue('dataLoadLimit', 100) as number;
    this.pageSize = limit;
    const distinct = this.getConfigValue('distinct', false) as boolean;
    const orderByConfig = this.getConfigValue('orderBy', []) as any[];
    const orderBy: SortCriteria[] = orderByConfig.map(s => ({ fieldId: s.fieldId, direction: s.direction }));
    this.dynamicTableService.selectEntries(datasetId, limit, offset, filters, distinct, orderBy)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Convert the response data to the format expected by the table
          this.tableData = this.stringifyRowData(response.rowData);
          this.filteredData = [...this.tableData]; // No client-side filtering needed
          this.applySorting();
          this.isLoadingData = false;

          // Update pagination state
          this.totalRecords = response.totalCount || response.rowData.length;
          this.paginationEnabled = this.totalRecords > limit;

          // Restore selection state after data is loaded
          this.restoreSelectionState();

          // Auto-select first row if configured, row selection is enabled, nothing was restored, and data is present
          if (this.rowSelectionEnabled
              && this.getConfigValue('autoSelectFirst', false)
              && this.selectedRowIndex === null
              && this.filteredData.length > 0) {
            this.selectedRowIndex = 0;
            this.storeRowInContext(this.filteredData[0]);
          }

          // Store all rows in context if configured
          this.emitAllRowsToContext();
        },
        error: (error) => {
          // console.error('Error loading table data:', error);
          // Fall back to mock data if real data fails to load
          this.tableData = this.generateMockData();
          this.filteredData = [...this.tableData];
          this.applySorting();
          this.isLoadingData = false;
        }
      });
  }

  onPageChange(page: number): void {
    if (!this.currentDatasetId) return;
    this.currentPage = page;
    const offset = page * this.pageSize;
    this.selectedRowIndex = null;
    this.loadTableData(this.currentDatasetId, offset);
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  // Convert all field values to strings (similar to Data Entry component)
  private stringifyRowData(rowData: any[]): TableData[] {
    return rowData.map(row => {
      const stringifiedRow: TableData = {};
      for (const key in row) {
        if (row.hasOwnProperty(key)) {
          stringifiedRow[key] = row[key] === null || row[key] === undefined ? '' : row[key].toString();
        }
      }
      return stringifiedRow;
    });
  }

  private generateMockData(): TableData[] {
    // Generate more rows to test scrolling (25 rows)
    const mockData: TableData[] = [];
    for (let i = 0; i < 25; i++) {
      mockData.push({});
    }

    // Fill in data for each column and row
    this.columns.forEach((col, colIndex) => {
      mockData.forEach((row, rowIndex) => {
        switch (col.dataType.toLowerCase()) {
          case 'string':
          case 'text':
            row[col.fieldName] = `${col.displayName} Row ${rowIndex + 1}`;
            break;
          case 'number':
          case 'int':
          case 'decimal':
            row[col.fieldName] = (rowIndex + 1) * 10 + colIndex;
            break;
          case 'date':
          case 'datetime':
            row[col.fieldName] = new Date(2024, 0, rowIndex + 1);
            break;
          case 'boolean':
            row[col.fieldName] = rowIndex % 2 === 0 ? 'Yes' : 'No';
            break;
          default:
            row[col.fieldName] = `${col.fieldName} ${rowIndex + 1}`;
        }
      });
    });

    return mockData;
  }

  // Filter dialog methods
  openFilterDialog(): void {
    this.dialog.open(this.filterDialog, {
      width: '40vw',
      maxHeight: '80vh',
      panelClass: 'filter-dialog'
    });
  }

  // Filter row methods (similar to Data Entry component)
  addFilterRow(): void {
    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      value2: '',
      dateValue: null,
      dateValue2: null,
      matchMode: 'exact',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeFilterRow(index: number): void {
    this.filterRows.splice(index, 1);
  }

  onFilterFieldChange(filterRow: any): void {
    // When a field is selected, store its fieldId and dataType
    const schema = this.datasetSchemas.find(s => s.fieldName === filterRow.fieldName);
    if (schema) {
      filterRow.fieldId = schema.fieldId || null;
      filterRow.dataType = schema.dataType || DataSchemaDataType.String;

      // Cache available match modes for this data type
      filterRow.availableMatchModes = this.getAvailableMatchModesForField(filterRow.dataType);

      // Set default match mode based on data type
      if (this.isNumericOrDateType(filterRow.dataType)) {
        filterRow.matchMode = 'exact';
      } else {
        filterRow.matchMode = 'partial';  // Default to partial for strings
      }

      // Clear values when field changes
      filterRow.value = '';
      filterRow.value2 = '';
      filterRow.dateValue = null;
      filterRow.dateValue2 = null;
    }
  }

  onMatchModeChange(filterRow: any): void {
    // Clear value2 and dateValue2 if not in 'between' mode
    if (filterRow.matchMode !== 'between') {
      filterRow.value2 = '';
      filterRow.dateValue2 = null;
    }
  }

  onFilterDateChange(filterRow: any, isSecondValue: boolean = false): void {
    // Convert date to ISO string format for the filter value
    const date = isSecondValue ? filterRow.dateValue2 : filterRow.dateValue;
    if (date) {
      const formatted = this.formatDateForFilter(date);
      if (isSecondValue) {
        filterRow.value2 = formatted;
      } else {
        filterRow.value = formatted;
      }
    } else {
      if (isSecondValue) {
        filterRow.value2 = '';
      } else {
        filterRow.value = '';
      }
    }
  }

  formatDateForFilter(date: Date): string {
    // Format date as ISO string without time for filtering
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  getAvailableMatchModesForField(dataType: string): { label: string, value: MatchMode }[] {
    const stringModes = [
      { label: 'Starts with', value: 'starts_with' as MatchMode },
      { label: 'Contains', value: 'partial' as MatchMode },
      { label: 'Equals', value: 'exact' as MatchMode },
      { label: 'Not Equal', value: 'not_equal' as MatchMode }
    ];

    const numericModes = [
      { label: 'Equals', value: 'exact' as MatchMode },
      { label: 'Not Equal', value: 'not_equal' as MatchMode },
      { label: 'Greater than', value: 'greater_than' as MatchMode },
      { label: 'Less than', value: 'less_than' as MatchMode },
      { label: 'Between', value: 'between' as MatchMode }
    ];

    if (this.isNumericOrDateType(dataType)) {
      return numericModes;
    } else {
      return stringModes;
    }
  }

  isNumericOrDateType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal, DataSchemaDataType.DateTime];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isNumericType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isDateType(dataType: string): boolean {
    return dataType === DataSchemaDataType.DateTime;
  }

  isDateTimeFilterRow(filterRow: any): boolean {
    return filterRow.dataType === DataSchemaDataType.DateTime;
  }

  getAvailableFilterColumns(): TableColumn[] {
    const configuredFilterColumns: string[] = this.getConfigValue('filterColumns', []);
    if (configuredFilterColumns && configuredFilterColumns.length > 0) {
      // Start with configured filter columns in order
      const result = configuredFilterColumns
        .map(name => this.columns.find(col => col.fieldName === name))
        .filter((col): col is TableColumn => !!col);

      // Ensure preset filter fields are included even if not explicitly added
      const presetFields: string[] = this.getConfigValue('presetFilterFields', []);
      const resultNames = new Set(result.map(col => col.fieldName));
      for (const presetName of presetFields) {
        if (!resultNames.has(presetName)) {
          const col = this.columns.find(c => c.fieldName === presetName);
          if (col) result.push(col);
        }
      }

      return result;
    }
    return this.columns;
  }

  getMatchModeLabel(matchMode: string): string {
    const labels: { [key: string]: string } = {
      'exact': 'Equals', 'not_equal': 'Not Equal', 'partial': 'Contains',
      'starts_with': 'Starts With', 'greater_than': 'Greater Than',
      'less_than': 'Less Than', 'greater_than_or_equal': 'Greater Than or Equal',
      'less_than_or_equal': 'Less Than or Equal', 'between': 'Between'
    };
    return labels[matchMode] || matchMode;
  }

  getMatchModeAbbrev(matchMode: string): string {
    const abbrevs: { [key: string]: string } = {
      'exact': '=', 'not_equal': '≠', 'partial': '∋',
      'starts_with': 'A…', 'greater_than': '>',
      'less_than': '<', 'greater_than_or_equal': '≥',
      'less_than_or_equal': '≤', 'between': '⟷'
    };
    return abbrevs[matchMode] || '=';
  }

  onInlineMatchModeChange(filterRow: any, matchMode: MatchMode): void {
    filterRow.matchMode = matchMode;
    this.applyInlineFilters();
  }

  getRowBackgroundColor(rowIndex: number): string | null {
    if (!this.getConfigValue('alternateRowColors', false)) return null;
    const evenColor = this.getConfigValue('rowEvenColor', '#f0f4f8');
    const oddColor = this.getConfigValue('rowOddColor', '#ffffff');
    return rowIndex % 2 === 0 ? oddColor : evenColor;
  }

  reloadData(): void {
    if (this.cell?.dataBinding?.datasetId && this.columns.length > 0) {
      // Columns already set up — just reload data with current filters
      this.loadTableData(this.cell.dataBinding.datasetId);
    } else if (this.cell?.dataBinding?.datasetId) {
      // No columns yet — do full setup
      this.currentDatasetId = null;
      this.loadDataset$.next(this.cell.dataBinding.datasetId);
    }
  }

  refreshTableData(): void {
    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  applyFilters(): void {
    this.hasActiveFilters = this.filterRows.some(row => {
      if (!row.fieldId) return false;
      if (this.isDateType(row.dataType)) {
        return row.dateValue != null;
      }
      return row.value?.trim();
    });

    this.saveFilterState();

    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  clearFilters(): void {
    this.filterRows = [];
    this.hasActiveFilters = false;
    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  // Inline filter methods
  addInlineFilter(): void {
    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      value2: '',
      dateValue: null,
      dateValue2: null,
      matchMode: 'exact',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeInlineFilter(index: number): void {
    this.filterRows.splice(index, 1);
    this.applyInlineFilters();
  }

  onInlineFilterFieldChange(filterRow: any): void {
    const schema = this.datasetSchemas.find(s => s.fieldName === filterRow.fieldName);
    if (schema) {
      filterRow.fieldId = schema.fieldId || null;
      filterRow.dataType = schema.dataType || DataSchemaDataType.String;
      filterRow.availableMatchModes = this.getAvailableMatchModesForField(filterRow.dataType);
      // Use configured inline match mode, or fall back to type-based default
      const configuredMatchMode = this.getConfigValue('inlineFilterMatchMode', '') as string;
      filterRow.matchMode = (configuredMatchMode || (this.isNumericOrDateType(filterRow.dataType) ? 'exact' : 'partial')) as MatchMode;
      filterRow.value = '';
      filterRow.value2 = '';
      filterRow.dateValue = null;
      filterRow.dateValue2 = null;
    }
  }

  onInlineFilterValueChange(): void {
    this.saveFilterState();
    this.inlineFilterApply$.next();
  }

  onInlineFilterDateChange(filterRow: any): void {
    if (filterRow.dateValue) {
      filterRow.value = this.formatDateForFilter(filterRow.dateValue);
    } else {
      filterRow.value = '';
    }
    this.applyInlineFilters();
  }

  applyInlineFilters(): void {
    this.hasActiveFilters = this.filterRows.some(row => {
      if (!row.fieldId) return false;
      if (this.isDateType(row.dataType)) {
        return row.dateValue != null;
      }
      return row.value?.trim();
    });

    this.saveFilterState();

    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  clearInlineFilters(): void {
    this.filterRows = [];
    this.hasActiveFilters = false;
    this.saveFilterState();
    if (this.currentDatasetId) {
      this.loadTableData(this.currentDatasetId);
    }
  }

  onRowClick(row: TableData, rowIndex: number): void {
    // Handle row selection for context data (independent of navigation)
    if (this.rowSelectionEnabled) {
      this.onRowSelect(row, rowIndex);
    }

    // Handle Row Upsert — single-row selection that writes (or clears) the target context
    if (this.getConfigValue('enableRowUpsert', false)) {
      this.onRowUpsertSelect(row, rowIndex);
    }

    // Handle navigation if enabled
    if (this.navigationEnabled) {
      // Store context data if navigation context storage is enabled
      if (this.getConfigValue('storeNavigationContext', false)) {
        this.storeRowInContext(row);
        this.navigationHighlightedRowIndex = rowIndex;
      }

      const navigationTargetTabId = this.getConfigValue('navigationTabId', '');

      if (navigationTargetTabId) {
        // Navigate to target tab
        this.tabNavigation.emit(navigationTargetTabId);
      }
    }
  }

  /**
   * Row Selection (update / add to existing context) — each click is independent:
   *   List target  → append the row to records (deduped — clicking the same row
   *                  again is a no-op, not a toggle).
   *   Single target → replace the context with the clicked row.
   * The clicked row is also marked as selectedRowIndex to give a visual cue
   * for "this is what you just clicked," but there's no toggle/deselect.
   */
  private onRowUpsertSelect(row: TableData, rowIndex: number): void {
    this.selectedRowIndex = rowIndex;
    this.writeUpsertTarget(row);
  }

  /** Writes the row into the configured upsert target. Append for list, replace for single. */
  private writeUpsertTarget(row: TableData): void {
    const target = this.getConfigValue<string>('rowUpsertContextVariable', '');
    if (!target || !this.viewManager) return;
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    const existing = this.viewManager.contextData[target];
    // List contexts are bare arrays (a legacy { records: [...] } wrapper still reads).
    const existingRecords = toContextArray(existing);

    // Project the row through configured field mappings (if any). Without
    // mappings, the row is written verbatim.
    const projected = this.projectRowForUpsert(row);
    // Mark as a new record so the action cell's auto saveMode routes this
    // through INSERT (not UPDATE) on save — the target backend table doesn't
    // know about this row yet.
    const rowWithMetadata = {
      ...projected,
      __isNew: true,
      __metadata: {
        datasetId: this.cell?.dataBinding?.datasetId,
        fieldReferences: this.buildFieldReferences(),
        sourcedByUpsert: true,
        isNewRecord: true
      }
    };

    if (existingRecords) {
      if (this.containsUpsertRecord(existingRecords, projected)) return;
      const updated = [...existingRecords, rowWithMetadata];
      this.viewManager.contextData[target] = updated;
      this.contextDataChange.emit({ [target]: updated });
    } else {
      this.viewManager.contextData[target] = rowWithMetadata;
      this.contextDataChange.emit({ [target]: rowWithMetadata });
    }
  }

  private projectRowForUpsert(row: TableData): any {
    const mappings = this.getConfigValue<any[]>('rowUpsertFieldMappings', []) || [];
    if (!mappings.length) return { ...row };
    const out: any = {};
    for (const m of mappings) {
      if (!m?.targetField) continue;
      if (m.sourceFields?.length) {
        const sep = m.separator ?? ' ';
        out[m.targetField] = m.sourceFields
          .map((f: string) => (row as any)[f])
          .filter((v: any) => v != null && v !== '')
          .join(sep);
      } else if (m.sourceField) {
        out[m.targetField] = (row as any)[m.sourceField];
      }
    }
    return out;
  }

  /** Cheap structural compare ignoring __metadata so a re-click on the same row is a no-op. */
  private containsUpsertRecord(records: any[], row: TableData): boolean {
    for (const existing of records) {
      if (!existing || typeof existing !== 'object') continue;
      const keys = new Set([...Object.keys(existing), ...Object.keys(row)].filter(k => !k.startsWith('__')));
      let match = true;
      for (const k of keys) {
        if ((existing as any)[k] !== (row as any)[k]) { match = false; break; }
      }
      if (match) return true;
    }
    return false;
  }

  onRowSelect(row: TableData, rowIndex: number): void {
    // Toggle selection (or select if different row)
    if (this.selectedRowIndex === rowIndex) {
      // Deselect if clicking the same row
      this.selectedRowIndex = null;
      this.clearContextData();
    } else {
      // Select new row
      this.selectedRowIndex = rowIndex;
      this.storeRowInContext(row);
    }
  }

  onCheckboxChange(row: TableData, rowIndex: number, isChecked: boolean): void {
    if (isChecked) {
      this.selectedRowIndex = rowIndex;
      this.storeRowInContext(row);
    } else {
      this.selectedRowIndex = null;
      this.clearContextData();
    }
  }

  private storeRowInContext(row: TableData): void {
    const contextObjects: {[contextKey: string]: any} = {};

    // 1. Handle primary context (stores the row data)
    const enablePrimaryContext = this.getConfigValue('enablePrimaryContext', false);
    const primaryContextVariable = this.getConfigValue('primaryContextVariable', '');

    if (enablePrimaryContext && primaryContextVariable) {
      contextObjects[primaryContextVariable] = {
        // Original row data
        ...row,
        // Metadata for context intelligence
        __metadata: {
          datasetId: this.cell.dataBinding.datasetId,
          fieldReferences: this.buildFieldReferences(),
          isPrimary: true
        }
      };
    }

    // 2. Additional contexts are loaded by the cascade mechanism in
    // refreshOrClearDependentContexts, which detects that these contexts depend
    // on the primary context and loads them via refreshContext using updated
    // context data to correctly resolve filter references.

    // 3. Fallback to legacy configuration for backward compatibility
    if (Object.keys(contextObjects).length === 0) {
      const legacyContextKey = this.getConfigValue('contextVariable', '');
      if (legacyContextKey) {
        contextObjects[legacyContextKey] = {
          // Original row data
          ...row,
          // Metadata for context intelligence
          __metadata: {
            datasetId: this.cell.dataBinding.datasetId,
            fieldReferences: this.buildFieldReferences(),
            isLegacy: true
          }
        };
      }
    }

    // Emit primary context immediately, additional contexts will be loaded asynchronously
    if (Object.keys(contextObjects).length > 0) {
      this.contextDataChange.emit(contextObjects);
    }
  }

  private loadAdditionalContexts(sourceRow: TableData, existingContexts: {[contextKey: string]: any}): void {
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];

    additionalContexts.forEach(additionalConfig => {
      this.loadSingleAdditionalContext(sourceRow, additionalConfig, existingContexts);
    });
  }

  private loadSingleAdditionalContext(
    sourceRow: TableData,
    config: ContextObjectConfig,
    existingContexts: {[contextKey: string]: any}
  ): void {
    // Check if we have cell filters configured (new approach)
    if (config.cellFilters && config.cellFilters.length > 0) {
      // Use cellFilters - pass sourceRow for 'firstSelectedRow' filter type.
      // A Single Record context fetches with limit 1, so an array-shaped context
      // must stay pinned to its first element — `IN (...)` with limit 1 would
      // return an arbitrary row instead of a deterministic one.
      const cellFilters = this.cellFilterService.buildCellFilters(
        config.cellFilters,
        this.viewManager,
        sourceRow,
        { multiValue: !config.loadFirstMatch }
      );
      const limit = config.loadFirstMatch ? 1 : 500;

      this.dynamicTableService.selectEntries(
        config.datasetId, limit, undefined, cellFilters || undefined, undefined, this.contextOrderBy(config))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => this.handleAdditionalContextResponse(response, config, existingContexts, null),
          error: (error) => this.handleAdditionalContextError(error, config, existingContexts)
        });
      return;
    }

    // Legacy approach: Use sourceField and filterFieldId
    const filterValue = sourceRow[config.sourceField];

    if (filterValue === null || filterValue === undefined) {
      // Emit empty context object preserving field structure (or list-shape wrapper for list mode)
      const updatedContexts = { ...existingContexts };
      updatedContexts[config.contextVariable] = this.createEmptyContextFromExisting(
        config.contextVariable, config.datasetId, config.loadFirstMatch !== true
      );
      this.contextDataChange.emit(updatedContexts);
      return;
    }

    // If no filter field specified, load all data from dataset
    if (!config.filterFieldId) {
      this.dynamicTableService.selectEntries(
        config.datasetId, config.loadFirstMatch ? 1 : -1, undefined, undefined, undefined, this.contextOrderBy(config))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => this.handleAdditionalContextResponse(response, config, existingContexts, filterValue),
          error: (error) => this.handleAdditionalContextError(error, config, existingContexts)
        });
    } else {
      // Apply filter using specified field
      const filters: DynamicTableFilters = {
        [config.filterFieldId]: {
          values: [filterValue],
          matchMode: 'exact'
        }
      };

      this.dynamicTableService.selectEntries(
        config.datasetId, config.loadFirstMatch ? 1 : -1, undefined, filters, undefined, this.contextOrderBy(config))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => this.handleAdditionalContextResponse(response, config, existingContexts, filterValue),
          error: (error) => this.handleAdditionalContextError(error, config, existingContexts)
        });
    }
  }

  private handleAdditionalContextResponse(
    response: any,
    config: ContextObjectConfig,
    existingContexts: {[contextKey: string]: any},
    filterValue: any
  ): void {
    const updatedContexts = { ...existingContexts };

    if (response.rowData && response.rowData.length > 0) {
      if (config.loadFirstMatch) {
        // Store the first matching record
        updatedContexts[config.contextVariable] = {
          ...response.rowData[0],
          __metadata: {
            datasetId: config.datasetId,
            sourceField: config.sourceField,
            filterFieldId: config.filterFieldId,
            filterValue: filterValue,
            isAdditional: true
          }
        };
      } else {
        // Store all matching records as a bare array — the standard list-context shape.
        updatedContexts[config.contextVariable] = response.rowData;
      }
    } else {
      // No matching records found — preserve list-shape if the context is a list
      updatedContexts[config.contextVariable] = this.createEmptyContextFromExisting(
        config.contextVariable, config.datasetId, config.loadFirstMatch !== true
      );
    }

    this.contextDataChange.emit(updatedContexts);
  }

  private handleAdditionalContextError(
    error: any,
    config: ContextObjectConfig,
    existingContexts: {[contextKey: string]: any}
  ): void {
    console.error(`Error loading additional context from dataset ${config.datasetId}:`, error);
    const updatedContexts = { ...existingContexts };
    updatedContexts[config.contextVariable] = this.createEmptyContextFromExisting(
      config.contextVariable, config.datasetId, config.loadFirstMatch !== true
    );
    this.contextDataChange.emit(updatedContexts);
  }

  private emitAllRowsToContext(): void {
    const storeAllRows = this.getConfigValue('storeAllRowsInContext', false);
    const contextVariable = this.getConfigValue('allRowsContextVariable', '');
    if (!storeAllRows || !contextVariable) return;

    this.contextDataChange.emit({ [contextVariable]: [...this.filteredData] });
  }

  private clearContextData(): void {
    const contextObjects: {[contextKey: string]: any} = {};

    // Clear primary context — reset to empty object with null fields
    const enablePrimaryContext = this.getConfigValue('enablePrimaryContext', false);
    const primaryContextVariable = this.getConfigValue('primaryContextVariable', '');

    if (enablePrimaryContext && primaryContextVariable) {
      contextObjects[primaryContextVariable] = this.createEmptyContextObject();
    }

    // Clear additional contexts — reset to empty objects preserving field structure
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    if (additionalContexts && additionalContexts.length > 0) {
      additionalContexts.forEach(additionalConfig => {
        if (additionalConfig.contextVariable && additionalConfig.contextVariable.trim()) {
          contextObjects[additionalConfig.contextVariable] = this.createEmptyContextFromExisting(
            additionalConfig.contextVariable, additionalConfig.datasetId, additionalConfig.loadFirstMatch !== true
          );
        }
      });
    }

    // Fallback to legacy single context variable for backward compatibility
    if (Object.keys(contextObjects).length === 0) {
      const legacyContextKey = this.getConfigValue('contextVariable', '');
      if (legacyContextKey) {
        contextObjects[legacyContextKey] = this.createEmptyContextObject();
      }
    }

    // Only emit if we have context objects to clear
    if (Object.keys(contextObjects).length > 0) {
      this.contextDataChange.emit(contextObjects);
    }
  }

  /**
   * Create an empty context object by looking at the current context's keys.
   * Used for additional contexts where the schema comes from a different dataset.
   *
   * When `isList` is true the empty value is a bare `[]` — the standard list-context
   * shape — so the context retains its list identity across selections that resolve
   * to zero rows: downstream cells (data entry tables, upsert targets, list display)
   * keep behaving like the context is a list.
   */
  private createEmptyContextFromExisting(contextVariable: string, datasetId?: number, isList: boolean = false): any {
    if (isList) {
      return [];
    }

    const existing = this.viewManager?.contextData?.[contextVariable];
    if (existing && typeof existing === 'object' && !isListContext(existing)) {
      const emptyObject: any = {};
      for (const key of Object.keys(existing)) {
        if (key === '__metadata') {
          emptyObject.__metadata = { ...existing.__metadata, isEmptyContext: true, isNewRecord: false };
        } else {
          emptyObject[key] = null;
        }
      }
      return emptyObject;
    }
    // No existing context to copy from — return a minimal empty context object
    // with metadata so downstream cells see an object, not null
    return {
      __metadata: {
        datasetId: datasetId || null,
        isEmptyContext: true,
        isNewRecord: false
      }
    };
  }

  private buildFieldReferences(): { [fieldName: string]: { referenceDataId?: number, referenceFieldId?: number } } {
    const fieldReferences: { [fieldName: string]: { referenceDataId?: number, referenceFieldId?: number } } = {};

    // Get the original schema data if available
    if (this.datasetSchemas) {
      this.datasetSchemas.forEach(schema => {
        if (schema.referenceDataId || schema.referenceFieldId) {
          fieldReferences[schema.fieldName] = {
            referenceDataId: schema.referenceDataId || undefined,
            referenceFieldId: schema.referenceFieldId || undefined
          };
        }
      });
    }

    return fieldReferences;
  }

  /**
   * A context's sort as the query wants it: field id and direction only, with rows that
   * never had a field chosen dropped.
   *
   * Pruned here rather than trusting what was saved, because a field id of 0 makes the whole
   * query fail and takes the context's data with it.
   */
  private contextOrderBy(config: ContextObjectConfig): SortCriteria[] | undefined {
    const sorts = (config.sortBy || [])
      .filter(sort => !!sort.fieldId)
      .map(sort => ({ fieldId: sort.fieldId, direction: sort.direction } as SortCriteria));

    return sorts.length > 0 ? sorts : undefined;
  }

  private buildContextInstruction(config: ContextObjectConfig): any {
    const instruction: any = {
      fields: [], // Fields array (not used in selectEntries call)
      instructions: {
        datasetId: config.datasetId,
        filters: {} as DynamicTableFilters,
        limit: config.loadFirstMatch ? 1 : -1,
        // Carried with the limit, since which row "the first" is depends on the order
        orderBy: this.contextOrderBy(config)
      }
    };

    // Add dependency if this context depends on another
    if (config.sourceContextVariable && config.sourceContextVariable.trim()) {
      instruction.depContextObj = config.sourceContextVariable;
    }

    // Build the filters, keeping each filter's configured match mode and folding a
    // lower + upper bound on one field into a single 'between'
    const filters = this.cellFilterService.buildContextInstructionFilters(config.cellFilters, {
      fieldId: config.filterFieldId,
      sourceContextVariable: config.sourceContextVariable,
      sourceField: config.sourceField
    });

    // Track the first context a filter reads from as this instruction's dependency, so
    // the fetch waits until that context has data
    if (!instruction.depContextObj) {
      const contextFilter = (config.cellFilters || []).find(cellFilter =>
        cellFilter.enabled !== false && cellFilter.filterType === 'context' && cellFilter.contextObject);
      if (contextFilter) {
        instruction.depContextObj = contextFilter.contextObject;
      }
    }

    // Only set filters if we have any
    if (filters) {
      instruction.instructions.filters = filters;
    }

    return instruction;
  }

  get navigationEnabled(): boolean {
    return this.getConfigValue('enableNavigation', false);
  }

  get rowSelectionEnabled(): boolean {
    return this.getConfigValue('enableRowSelection', false);
  }

  get deleteEnabled(): boolean {
    return this.getConfigValue('allowDelete', false);
  }

  onDeleteRow(row: TableData, rowIndex: number, event: Event): void {
    event.stopPropagation();

    const datasetId = this.cell?.dataBinding?.datasetId;
    if (!datasetId) return;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      panelClass: 'center-dialog'
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (confirmed) {
          this.dynamicTableService.deleteEntries(datasetId, [row])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (success) => {
                if (success) {
                  // Clear selection if deleted row was selected
                  if (this.selectedRowIndex === rowIndex) {
                    this.selectedRowIndex = null;
                    this.clearContextData();
                  }

                  // Reload data from the server to stay in sync
                  if (this.currentDatasetId) {
                    this.loadTableData(this.currentDatasetId);
                  }
                }
              },
              error: (error) => {
                console.error('Error deleting row:', error);
              }
            });
        }
      });
  }

  isRowSelected(rowIndex: number): boolean {
    return this.selectedRowIndex === rowIndex;
  }

  isRowNavigationHighlighted(rowIndex: number): boolean {
    return this.navigationHighlightedRowIndex === rowIndex;
  }

  isRowNavigatable(): boolean {
    return this.navigationEnabled;
  }

  private restoreSelectionState(): void {
    if (!this.viewManager?.contextData || this.filteredData.length === 0) {
      return;
    }

    const contextKey = this.getConfigValue('contextVariable', 'selectedRow');
    const storedData = this.viewManager.contextData[contextKey];

    if (storedData) {
      // Find the row that matches the stored data
      const matchingRowIndex = this.filteredData.findIndex(row => {
        // Compare all properties to find exact match
        return Object.keys(storedData).every(key => {
          return row[key] === storedData[key];
        });
      });

      if (matchingRowIndex >= 0) {
        if (this.rowSelectionEnabled) {
          // Restore selection checkbox state
          this.selectedRowIndex = matchingRowIndex;
        } else if (this.navigationEnabled && this.getConfigValue('storeNavigationContext', false)) {
          // Restore navigation highlighting
          this.navigationHighlightedRowIndex = matchingRowIndex;
        }
      }
    }
  }

  onSortColumn(column: TableColumn): void {
    if (this.sortColumn === column.fieldName) {
      // Cycle: asc -> desc -> default
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      }
    } else {
      this.sortColumn = column.fieldName;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortColumn || !this.sortDirection) {
      this.filteredData = [...this.tableData];
      return;
    }
    const field = this.sortColumn;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredData.sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * dir;
      }
      return String(valA).localeCompare(String(valB)) * dir;
    });
  }

  trackByColumn(index: number, column: TableColumn): string {
    return column.fieldName;
  }

  formatDate(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return value.toString();
    return date.toLocaleDateString();
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return value.toString();
    return num.toLocaleString();
  }

  getPreviewColumns(): TableColumn[] {
    // Return first 4 columns for preview, or all if less than 4
    return this.columns.slice(0, Math.min(4, this.columns.length));
  }

  getPreviewData(): TableData[] {
    // Generate 3 rows of dummy preview data
    const previewData: TableData[] = [];
    const previewColumns = this.getPreviewColumns();

    for (let i = 1; i <= 3; i++) {
      const row: TableData = {};
      previewColumns.forEach(col => {
        switch (col.dataType.toLowerCase()) {
          case 'string':
          case 'text':
            row[col.fieldName] = `Sample ${i}`;
            break;
          case 'number':
          case 'int':
          case 'decimal':
            row[col.fieldName] = Math.floor(Math.random() * 100) + i;
            break;
          case 'date':
          case 'datetime':
            row[col.fieldName] = new Date(2024, i, i * 5).toLocaleDateString();
            break;
          case 'boolean':
            row[col.fieldName] = i % 2 === 0 ? 'Yes' : 'No';
            break;
          default:
            row[col.fieldName] = `Data ${i}`;
        }
      });
      previewData.push(row);
    }

    return previewData;
  }

  getDisplayedColumns(): TableColumn[] {
    // Return columns in the configured display order
    return this.displayedColumns
      .map(name => this.columns.find(col => col.fieldName === name))
      .filter((col): col is TableColumn => !!col);
  }

  isColumnDisplayed(columnName: string): boolean {
    return this.displayedColumns.includes(columnName);
  }

  // Create New Entry Configuration Getters
  get allowCreate(): boolean {
    return this.getConfigValue('allowCreate', false);
  }

  get createMode(): 'inline' | 'navigate' {
    return this.getConfigValue('createMode', 'inline');
  }

  get createButtonLabel(): string {
    return this.getConfigValue('createButtonLabel', '') || 'Create New';
  }

  // Create New Entry Methods
  onCreateClick(): void {
    if (this.createMode === 'inline') {
      this.openInlineCreateDialog();
    } else {
      this.navigateWithEmptyContexts();
    }
  }

  private openInlineCreateDialog(): void {
    if (!this.cell?.dataBinding?.datasetId) return;

    // Use the LookupDialogComponent for inline creation
    // Get display columns from configuration or use all columns
    const displayColumns = this.displayedColumns.length > 0
      ? this.displayedColumns
      : this.columns.map(c => c.fieldName);

    // Find a suitable value and display field
    const valueField = this.columns.length > 0 ? this.columns[0].fieldName : '';
    const displayField = this.columns.length > 1 ? this.columns[1].fieldName : valueField;

    const dialogData: LookupDialogData = {
      datasetId: this.cell.dataBinding.datasetId,
      displayColumns: displayColumns,
      filterColumns: displayColumns, // Allow filtering on all displayed columns
      valueField: valueField,
      displayField: displayField,
      cellFilters: this.cell.dataBinding.cellFilters,
      viewManager: this.viewManager || undefined,
      title: 'Create New Entry',
      allowCreate: true,
      startInCreateMode: true, // Skip the search view and go directly to create form
      excludedFields: this.getConfigValue('createExcludedFields', []),
      prePopulatedFields: this.getConfigValue('createPrePopulatedFields', [])
    };

    const dialogRef = this.dialog.open(LookupDialogComponent, {
      width: '80vw',
      maxWidth: '1200px',
      maxHeight: '90vh',
      data: dialogData,
      autoFocus: false
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: LookupDialogResult | undefined) => {
        if (result && result.selectedRow) {
          // Refresh the table data to show the newly created entry
          if (this.currentDatasetId) {
            this.loadTableData(this.currentDatasetId);
          }

          // Select the newly created row in the filter list
          // We'll look for it after the data reloads
          setTimeout(() => {
            this.selectNewlyCreatedRow(result.selectedRow);
          }, 500);
        } else {
          // Even if no selection was made, refresh to show any newly created entries
          if (this.currentDatasetId) {
            this.loadTableData(this.currentDatasetId);
          }
        }
      });
  }

  private selectNewlyCreatedRow(createdRow: any): void {
    // Find the row in the filtered data that matches the created row
    const matchIndex = this.filteredData.findIndex(row => {
      // Match by comparing field values
      return Object.keys(createdRow).every(key => {
        if (key === '__metadata') return true;
        return row[key] === createdRow[key] || String(row[key]) === String(createdRow[key]);
      });
    });

    if (matchIndex >= 0) {
      this.selectedRowIndex = matchIndex;
      this.storeRowInContext(this.filteredData[matchIndex]);
    }
  }

  private navigateWithEmptyContexts(): void {
    const createEmptyContexts = this.getConfigValue('createEmptyContexts', []) as CreateEmptyContextConfig[];
    const navigateTabId = this.getConfigValue('createNavigateTabId', '');

    if (!navigateTabId || createEmptyContexts.length === 0) {
      console.warn('Navigate mode requires both a target tab and at least one empty context configuration');
      return;
    }

    // Build empty context objects for each configured empty context
    this.buildEmptyContextsAndNavigate(createEmptyContexts, navigateTabId);
  }

  private buildEmptyContextsAndNavigate(configs: CreateEmptyContextConfig[], navigateTabId: string): void {
    // First, load dataset schemas for all configured datasets
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const contextObjects: { [contextKey: string]: any } = {};

          configs.forEach(config => {
            const dataset = datasetsMap.get(config.datasetId);
            const isList = config.contextType === 'list';

            if (isList) {
              // Empty list context — a bare array, matching what additionalContexts
              // produce in list mode. Rows added later carry their own __metadata.
              contextObjects[config.contextVariable] = [];
              return;
            }

            if (dataset) {
              // Build an empty object based on the dataset schema
              const emptyObject: any = {
                __isNew: true, // Mark as a new record
                __metadata: {
                  datasetId: config.datasetId,
                  isNewRecord: true,
                  isEmptyContext: true, // Flag to indicate this was created as an empty context for create-new
                  fieldReferences: this.buildFieldReferencesForDataset(dataset.dataTableSchemas)
                }
              };

              // Initialize all fields to null/default values based on schema
              dataset.dataTableSchemas.forEach(schema => {
                if (!schema.isHidden) {
                  const isAutoGenerated = schema.fieldName === 'Hidden_Internal_PrimaryKey' ||
                                          (schema.isPrimaryKey && schema.fieldName.toLowerCase().includes('internal'));

                  if (!isAutoGenerated) {
                    // Set default values based on data type
                    emptyObject[schema.fieldName] = this.getDefaultValueForType(schema.dataType.toString());
                  }
                }
              });

              contextObjects[config.contextVariable] = emptyObject;
            } else {
              console.warn(`Dataset ${config.datasetId} not found for context ${config.contextVariable}`);
              // Create a minimal empty context even if dataset isn't found
              contextObjects[config.contextVariable] = {
                __isNew: true,
                __metadata: {
                  datasetId: config.datasetId,
                  isNewRecord: true,
                  isEmptyContext: true
                }
              };
            }
          });

          // Emit all the empty contexts
          if (Object.keys(contextObjects).length > 0) {
            this.contextDataChange.emit(contextObjects);
          }

          // Wait for any auxiliary additional-context loads to complete before
          // navigating — otherwise the destination tab can tear down this cell and
          // the in-flight requests get cancelled. forkJoin always emits exactly once
          // here because each inner source is wrapped in catchError → of(null), so
          // we navigate in the `next` handler.
          this.triggerCreateNewAdditionalContextLoads()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.tabNavigation.emit(navigateTabId));
        },
        error: (error) => {
          console.error('Error loading datasets for empty contexts:', error);
          // Still navigate, but with minimal empty contexts
          const contextObjects: { [contextKey: string]: any } = {};
          configs.forEach(config => {
            if (config.contextType === 'list') {
              contextObjects[config.contextVariable] = [];
            } else {
              contextObjects[config.contextVariable] = {
                __isNew: true,
                __metadata: {
                  datasetId: config.datasetId,
                  isNewRecord: true,
                  isEmptyContext: true
                }
              };
            }
          });

          if (Object.keys(contextObjects).length > 0) {
            this.contextDataChange.emit(contextObjects);
          }

          this.triggerCreateNewAdditionalContextLoads()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.tabNavigation.emit(navigateTabId));
        }
      });
  }

  /**
   * Returns an Observable that fires row-independent loads for any additional contexts
   * flagged with `loadOnCreateNew`, emits exactly once when all loads have completed
   * (or there were none), then completes. Each load's response is fed into contextData
   * via the same path the row-click flow uses. Errors are swallowed per-load so a single
   * failure doesn't block navigation.
   *
   * Navigation is gated on this observable's emission so the destination tab doesn't tear
   * down the filter list cell mid-flight (which would cancel the underlying HTTP requests).
   */
  private triggerCreateNewAdditionalContextLoads(): Observable<any> {
    const additionalContexts = this.getConfigValue('additionalContexts', []) as ContextObjectConfig[];
    if (!additionalContexts || additionalContexts.length === 0) return of(null);

    const loadStreams: Observable<any>[] = [];
    additionalContexts.forEach(config => {
      if (!config.loadOnCreateNew || !config.datasetId) return;
      if (this.isAdditionalContextRowDependent(config)) return;

      const usableCellFilters = (config.cellFilters || []).filter(cf => cf.filterType !== 'firstSelectedRow');
      const builtFilters = usableCellFilters.length > 0
        ? this.cellFilterService.buildCellFilters(usableCellFilters, this.viewManager, null, { multiValue: !config.loadFirstMatch })
        : null;

      const limit = config.loadFirstMatch ? 1 : (usableCellFilters.length > 0 ? 500 : -1);

      loadStreams.push(
        this.dynamicTableService.selectEntries(
          config.datasetId, limit, undefined, builtFilters || undefined, undefined, this.contextOrderBy(config))
          .pipe(
            tap(response => this.handleAdditionalContextResponse(response, config, {}, null)),
            catchError(error => {
              this.handleAdditionalContextError(error, config, {});
              return of(null);
            })
          )
      );
    });

    if (loadStreams.length === 0) return of(null);
    return forkJoin(loadStreams);
  }

  /** Mirror of the builder-side check — see filter-list-cell-config.component.ts. */
  private isAdditionalContextRowDependent(config: ContextObjectConfig): boolean {
    if (config.cellFilters && config.cellFilters.length > 0) {
      return config.cellFilters.some(cf => cf.filterType === 'firstSelectedRow' && cf.enabled !== false);
    }
    return !!(config.filterFieldId && config.sourceField);
  }

  private buildFieldReferencesForDataset(schemas: DataTableSchema[]): { [fieldName: string]: { referenceDataId?: number, referenceFieldId?: number } } {
    const fieldReferences: { [fieldName: string]: { referenceDataId?: number, referenceFieldId?: number } } = {};

    schemas.forEach(schema => {
      if (schema.referenceDataId || schema.referenceFieldId) {
        fieldReferences[schema.fieldName] = {
          referenceDataId: schema.referenceDataId || undefined,
          referenceFieldId: schema.referenceFieldId || undefined
        };
      }
    });

    return fieldReferences;
  }

  private getDefaultValueForType(dataType: string): any {
    switch (dataType.toLowerCase()) {
      case 'string':
      case 'text':
        return '';
      case 'integer':
      case 'decimal':
      case 'number':
        return null;
      case 'boolean':
        return false;
      case 'datetime':
        return null;
      default:
        return null;
    }
  }
}
